import { Col, Row } from 'antd'
import React, { useEffect, useState } from 'react'
import {
  Controller,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputField } from '@shared/InputField'
import RadioButtonGroup from '@shared/RadioButtonGroup'
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { useDebounceCallback } from '@shared/hooks/useDebounceCallback'
import { orderStatusListType } from 'store/types/salaryCard'
import moment from 'moment'
import { StatusType } from '../../constants/fieldValues.constant'
import {
  SalaryCardListRequest,
  useLazyFetchSalaryCardListQuery,
  useLazyFetchSalaryCardStatusListQuery,
} from 'store/reducers/salaryCards/salaryCardList.api'
import { secureLocalStorageGet } from '../../../../../helpers'
import SelectGlobal from '@shared/Select'
import FilterDateInputs from '@shared/FilterDateInput/FilterDateInputs'
import { useFetchBusinessDateMutation } from '@app/store/reducers/configs/businessDate.api'

function getDefaultStatus(options: orderStatusListType[]) {
  const role = secureLocalStorageGet('rol')

  const MUST_BE_SIGNED = options.find(
    (i) => i.status === StatusType.PENDING_SIGNER
  )
  const CLOSED = options.find((i) => i.status === StatusType.CLOSED)

  if (CLOSED) return StatusType.CLOSED
  if (MUST_BE_SIGNED && role === 2) return StatusType.FOR_MUST_BE_SIGNED

  return options?.[0]?.status
}

function SalaryCardFilterBar() {
  const { t } = useTranslation()
  const [localOrderStatusList, setLocalOrderStatusList] = useState<
    orderStatusListType[]
  >([])

  const [
    fetchSalaryCardOrderstatus,
    { isSuccess, isFetching, isLoading, data: orderStatusList = [] },
  ] = useLazyFetchSalaryCardStatusListQuery()

  const [mutate] = useLazyFetchSalaryCardListQuery()

  const {
    setValue,
    getValues,
    resetField,
    control,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<SalaryCardListRequest>()

  const {
    fieldState: { invalid: startDateInValid, error: startError },
  } = useController({ control, name: 'startDate' })
  const {
    fieldState: { invalid: endDateInValid, error: endError },
  } = useController({ control, name: 'endDate' })

  const debounced = useDebounceCallback((e) => {
    setValue('searchText', e)
  }, 500)

  const startDate = useWatch({
    control,
    name: 'startDate',
  })
  const endDate = useWatch({
    control,
    name: 'endDate',
  })
  const page = useWatch({ control, name: 'page', defaultValue: 1 })
  const pageCount = useWatch({ control, name: 'pageCount', defaultValue: 10 })
  const searchText = useWatch({ control, name: 'searchText' })
  const status = useWatch({ control, name: 'status' })

  useEffect(() => {
    setValue('page', 1, { shouldValidate: true })
  }, [pageCount, status])

  useEffect(() => {
    ;(async () => {
      const isValid = await trigger(['startDate', 'endDate'])
      setValue('status', '', { shouldValidate: true })

      if (isValid) {
        fetchSalaryCardOrderstatus({
          searchText,
          startDate,
          endDate,
        })
      }
    })()
  }, [startDate, endDate])

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setLocalOrderStatusList(orderStatusList)

      const defaultStatus = getDefaultStatus(orderStatusList)
      setValue('status', defaultStatus, { shouldValidate: true })
    }
  }, [isFetching, isSuccess])

  useEffect(() => {
    ;(async () => {
      handleSubmit((data) => {
        mutate({
          ...data,
          status:
            data.status === StatusType.FOR_MUST_BE_SIGNED
              ? StatusType.PENDING_SIGNER
              : data.status,
        })
      })()
    })()
  }, [searchText, status, page, pageCount])

  const [fetchBusinessDate] = useFetchBusinessDateMutation({
    fixedCacheKey: 'businessDate',
  })

  useEffect(() => {
    fetchBusinessDate()
  }, [])

  return (
    <fieldset disabled={isSubmitting}>
      <Row style={{ flexDirection: 'column', gap: 8 }}>
        <Col span={24}>
          <Row wrap={true} justify="space-between">
            <Col span={12}>
              <FilterDateInputs
                startDateError={startDateInValid}
                startDateErrorMessage={startError?.message}
                endDateError={endDateInValid}
                endDateErrorMessage={endError?.message}
                value="thisWeek"
                onChangePicker={(value) => {
                  setValue(
                    'startDate',
                    value.dateFrom?.format('DD.MM.YYYY') as string,
                    {
                      shouldValidate: true,
                    }
                  )
                  setValue(
                    'endDate',
                    value.dateTo?.format('DD.MM.YYYY') as string,
                    {
                      shouldValidate: true,
                    }
                  )
                }}
              />
            </Col>
            <Col span={6}>
              <Controller
                control={control}
                name="searchText"
                render={({
                  field: { onChange, value, ...fields },
                  fieldState: { isDirty },
                }) => {
                  return (
                    <InputField
                      placeholder={t(
                        'orders.salaryCardOrder.inputLabels.minimalSymbol'
                      )}
                      label={t('orders.salaryCardOrder.inputLabels.search')}
                      wrapperCol={{ span: 16 }}
                      labelCol={{ span: 8 }}
                      suffix={
                        !isDirty ? (
                          <span className="anticon anticon-eye-invisible ant-input-password-icon">
                            <SearchOutlined />
                          </span>
                        ) : (
                          <span
                            onClick={() => resetField('searchText')}
                            className="anticon anticon-eye-invisible ant-input-password-icon ant-typography ant-typography-danger"
                          >
                            <CloseOutlined />
                          </span>
                        )
                      }
                      onChange={(value) => debounced(value)}
                    />
                  )
                }}
              />
            </Col>
            {/* <Col span={6}>
              <FilterActionButtons />
            </Col> */}
          </Row>
        </Col>
        <Col span={24}>
          <Row justify="space-between">
            <Col>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <RadioButtonGroup
                    options={localOrderStatusList}
                    label={t('common.status')}
                    loading={isLoading}
                    {...field}
                  />
                )}
              />
            </Col>

            <Col span={5}>
              <Controller
                control={control}
                name="pageCount"
                render={({ field }) => (
                  <SelectGlobal
                    style={{ margin: 0 }}
                    label={t('salary.numberAtPage')}
                    labelCol={{ span: 20 }}
                    selectType={true}
                    defaultValue={10}
                    options={[10, 20, 50].map((item) => ({
                      label: item,
                      value: item,
                    }))}
                    {...field}
                  />
                )}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </fieldset>
  )
}

export default SalaryCardFilterBar
